import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DangerButton,
  Divider,
  FormField,
  Input,
  IconButton,
  PrimaryButton,
  RadioGroup,
  SearchInput,
  SecondaryButton,
  Select,
  Checkbox,
  CopyIcon,
  debounce,
  ShuffleIcon,
} from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import {
  useCouponSearch,
  useCouponsList,
  useCreateCoupon,
  useCreateCouponsBulk,
  useDeactivateCoupon,
} from "../hooks/useAdminCoupons";
import { useAffiliateUsers } from "../hooks/useAdminAffiliates";
import CouponDefaultTemplateCard from "../components/CouponDefaultTemplateCard";
import { setCouponAffiliates } from "../utils/adminApi";
import {
  BULK_CREATE_MAX_COUNT,
  BULK_CREATE_MAX_REDEMPTIONS_EXCLUSIVE,
  COUPON_CODE_LENGTH,
  generateCouponCode,
} from "../utils/couponCodeUtils";

const DEFAULT_LIMIT = 100;
const DEFAULT_CURRENCY = "USD";
const DEFAULT_MULTI_COUNT = "5";

const formatIsoDateTime = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
};

const formatDiscount = (coupon) => {
  if (!coupon) return "—";
  if (coupon.percent_off != null) return `${coupon.percent_off}%`;
  if (coupon.amount_off != null) return `${coupon.amount_off} ${coupon.currency || DEFAULT_CURRENCY}`;
  return "—";
};

const formatRedemptions = (coupon) => {
  if (!coupon) return "—";
  const used = coupon.times_redeemed ?? 0;
  const max = coupon.max_redemptions ?? null;
  if (max == null) return `${used}`;
  return `${used}/${max}`;
};

const toggleArrayValue = (arr, value) => {
  const next = new Set(arr || []);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return Array.from(next);
};

const uniqById = (arr = []) => {
  const byId = new Map();
  for (const u of arr || []) {
    if (u && u.id != null) byId.set(String(u.id), u);
  }
  return Array.from(byId.values());
};

// Ready-to-send message for the coupon's affiliate. When the affiliate account
// was pre-created by an admin (must_complete_onboarding), the message also walks
// them through claiming the account; the temporary password is never available
// here, so the admin pastes it over the placeholder before sending.
const buildAffiliateCouponMessage = ({ coupon, affiliateUsername, origin, includeAccountSetup }) => {
  const code = coupon?.code || "";
  const link = `${origin}/discount/${code}`;

  const benefits = [];
  // Access coupons are about the free-access grant only — no discount/renewal talk.
  if (!coupon?.is_access_coupon) {
    if (coupon?.percent_off != null) {
      benefits.push(`${coupon.percent_off}% off${coupon.include_renewals ? " on every renewal" : " on the first invoice"}`);
    } else if (coupon?.amount_off != null) {
      benefits.push(
        `${coupon.amount_off} ${coupon.currency || DEFAULT_CURRENCY} off${coupon.include_renewals ? " on every renewal" : " on the first invoice"}`
      );
    }
    if (Number(coupon?.free_trial_days || 0) > 0) benefits.push(`${coupon.free_trial_days} free trial days`);
  }
  if (coupon?.free_access_plan && coupon?.free_access_until) {
    const untilDate = new Date(coupon.free_access_until);
    const untilLabel = Number.isNaN(untilDate.getTime()) ? coupon.free_access_until : untilDate.toLocaleDateString();
    benefits.push(`free access to the ${String(coupon.free_access_plan).toUpperCase()} plan until ${untilLabel} (no payment details needed)`);
  } else if (coupon?.free_access_plan && Number(coupon?.free_access_days || 0) > 0) {
    const days = Number(coupon.free_access_days);
    benefits.push(
      `free access to the ${String(coupon.free_access_plan).toUpperCase()} plan for ${days} day${days === 1 ? "" : "s"} (no payment details needed)`
    );
  }

  const lines = [
    `Hi${affiliateUsername ? ` ${affiliateUsername}` : ""}!`,
    "",
    `Your MyVitae coupon is ready: ${code}`,
  ];
  if (benefits.length) lines.push(`It gives ${benefits.join(" + ")}.`);
  lines.push(
    "",
    `Share this link with your audience: ${link}`,
    "Anyone who registers or subscribes through it is credited to you.",
    ""
  );

  if (includeAccountSetup) {
    lines.push(
      "Your affiliate account has already been created for you:",
      `- Username: ${affiliateUsername}`,
      "- Temporary password: [PASTE TEMPORARY PASSWORD HERE]",
      "",
      `1. Sign in at ${origin}/affiliate/login`,
      "2. On your first access you'll choose your own password and confirm your email (you can also change your username).",
      `3. Track your coupon's performance at ${origin}/affiliate/dashboard/analytics`,
      ""
    );
  } else {
    lines.push(
      `Track your coupon's performance anytime at ${origin}/affiliate/dashboard/analytics (sign in at ${origin}/affiliate/login).`,
      ""
    );
  }

  lines.push("Thank you!", "MyVitae Team");
  return lines.join("\n");
};

const CouponsAdminPage = () => {
  // Filters
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [activeState, setActiveState] = useState("all");

  // Cursor pagination (aggregated list)
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [listVersion, setListVersion] = useState(0);

  // Selection / details
  const [selectedCouponId, setSelectedCouponId] = useState(null);

  // Create form state
  const [form, setForm] = useState({
    code: "",
    discountType: "percent", // 'percent' | 'amount' | 'trial'
    percent_off: "",
    amount_off: "",
    currency: DEFAULT_CURRENCY,
    include_renewals: false,
    free_trial_days: "0",
    redeem_by: "",
    max_redemptions: "",
    first_time_only: false,
    plans: [],
    billing_periods: [],
    active: true,
    affiliate_ids: [],
    free_access_mode: "none", // 'none' | 'until' | 'days'
    free_access_until: "",
    free_access_days: "",
    free_access_plan: "",
    is_access_coupon: false,
    linked_coupon_id: null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [notice, setNotice] = useState(null); // { type: 'success'|'error'|'warning', message: string, details?: string }

  // Bulk create: count sits next to the multi button (not a form field). Default 5, cap 20.
  const [multiCount, setMultiCount] = useState(DEFAULT_MULTI_COUNT);

  // Create: affiliate typeahead
  const [createAffiliateQ, setCreateAffiliateQ] = useState("");
  const affiliateCreateQuery = useAffiliateUsers({
    q: createAffiliateQ,
    only_affiliates: true,
    limit: 20,
    enabled: (createAffiliateQ || "").trim().length >= 2,
  });
  const createAffiliateResults = affiliateCreateQuery.data?.results || [];

  // Create: linked-coupon typeahead (mirrors the affiliate typeahead pattern).
  const [linkedCouponQ, setLinkedCouponQ] = useState("");
  const [linkedCouponSelection, setLinkedCouponSelection] = useState(null); // { id, code }
  const linkedCouponQuery = useCouponSearch({
    q: linkedCouponQ,
    limit: 20,
    enabled: (linkedCouponQ || "").trim().length >= 2,
  });
  const linkedCouponResults = linkedCouponQuery.data?.results || [];

  // Deactivate confirm modal
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);

  // Warning confirm: creating a free-access coupon that is NOT flagged as access coupon.
  // Holds "single" | "bulk" for which create action to run on confirm.
  const [pendingAccessConfirm, setPendingAccessConfirm] = useState(null);

  const couponsQuery = useCouponsList({
    cursor,
    limit: DEFAULT_LIMIT,
    q,
    active_state: activeState,
    list_version: listVersion,
  });
  const createMutation = useCreateCoupon();
  const createBulkMutation = useCreateCouponsBulk();
  const deactivateMutation = useDeactivateCoupon();

  const isCreateBusy = createMutation.isPending || createBulkMutation.isPending;

  const debouncedSetQ = useMemo(() => debounce((value) => setQ(value), 350), []);

  const resetList = () => {
    setCursor(null);
    setNextCursor(null);
    setCoupons([]);
    setSelectedCouponId(null);
    setListVersion((v) => v + 1);
  };

  // Filter changes reset cursor + aggregated results
  useEffect(() => {
    resetList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activeState]);

  // Merge query results into aggregated list
  useEffect(() => {
    const data = couponsQuery.data;
    if (!data?.results) return;

    setNextCursor(data.next_cursor || null);
    setCoupons((prev) => {
      const incoming = data.results || [];
      if (!cursor) return incoming;

      const byId = new Map((prev || []).map((c) => [c.id, c]));
      for (const c of incoming) byId.set(c.id, c);
      return Array.from(byId.values());
    });
  }, [couponsQuery.data, cursor]);

  const selectedCoupon = useMemo(() => coupons.find((c) => c.id === selectedCouponId) || null, [coupons, selectedCouponId]);
  const isDetailsOpen = Boolean(selectedCoupon);
  const tableColSpan = isDetailsOpen ? 5 : 10;

  // Details: affiliate edit state
  const [detailsAffiliateIds, setDetailsAffiliateIds] = useState([]);
  const [detailsAffiliateQ, setDetailsAffiliateQ] = useState("");
  const [detailsAffiliateSaving, setDetailsAffiliateSaving] = useState(false);
  const [detailsAffiliateNotice, setDetailsAffiliateNotice] = useState(null); // { type, message }
  const [isConfirmAffiliateSaveOpen, setIsConfirmAffiliateSaveOpen] = useState(false);
  const [copyNotice, setCopyNotice] = useState(null); // { type: 'success'|'error', message }

  useEffect(() => {
    setDetailsAffiliateQ("");
    setDetailsAffiliateNotice(null);
    setCopyNotice(null);
    setDetailsAffiliateIds(selectedCoupon?.influencer_ids || []);
  }, [selectedCouponId]); // eslint-disable-line react-hooks/exhaustive-deps

  const couponShareLink = useMemo(() => {
    if (!selectedCoupon?.code) return null;
    if (typeof window === "undefined") return null;
    return `${window.location.origin}/discount/${selectedCoupon.code}`;
  }, [selectedCoupon]);

  const detailsAffiliateOriginalIds = useMemo(() => selectedCoupon?.influencer_ids || [], [selectedCoupon]);
  const affiliateIdsAreDirty = useMemo(() => {
    const a = Array.from(new Set(detailsAffiliateOriginalIds || [])).map(String).sort();
    const b = Array.from(new Set(detailsAffiliateIds || [])).map(String).sort();
    if (a.length !== b.length) return true;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return true;
    }
    return false;
  }, [detailsAffiliateIds, detailsAffiliateOriginalIds]);

  const affiliateDetailsQuery = useAffiliateUsers({
    q: detailsAffiliateQ,
    only_affiliates: true,
    limit: 20,
    enabled: (detailsAffiliateQ || "").trim().length >= 2,
  });
  const detailsAffiliateResults = affiliateDetailsQuery.data?.results || [];

  const selectedCouponAffiliates = useMemo(() => {
    const fromCoupon = Array.isArray(selectedCoupon?.influencers) ? selectedCoupon.influencers : [];
    const merged = uniqById([...fromCoupon, ...createAffiliateResults, ...detailsAffiliateResults]);
    const byId = new Map(merged.map((u) => [String(u.id), u]));
    return (selectedCoupon?.influencer_ids || []).map((id) => byId.get(String(id)) || { id, username: `User #${id}` });
  }, [selectedCoupon, createAffiliateResults, detailsAffiliateResults]);

  const detailsDraftAffiliates = useMemo(() => {
    const fromCoupon = Array.isArray(selectedCoupon?.influencers) ? selectedCoupon.influencers : [];
    const merged = uniqById([...fromCoupon, ...detailsAffiliateResults]);
    const byId = new Map(merged.map((u) => [String(u.id), u]));
    return (detailsAffiliateIds || []).map((id) => byId.get(String(id)) || { id, username: `User #${id}` });
  }, [detailsAffiliateIds, selectedCoupon, detailsAffiliateResults]);

  // Copy-message: the linked affiliate may still need to claim their pre-created
  // account. The coupon serializer does not carry that flag, so when it is absent
  // we resolve it through the affiliate users list.
  const linkedAffiliate = selectedCouponAffiliates[0] || null;
  const linkedAffiliateLookupQuery = useAffiliateUsers({
    q: linkedAffiliate?.username || "",
    only_affiliates: true,
    limit: 20,
    enabled: Boolean(linkedAffiliate?.username) && linkedAffiliate?.must_complete_onboarding === undefined,
  });
  const linkedAffiliateIsProvisional = useMemo(() => {
    if (!linkedAffiliate) return false;
    if (linkedAffiliate.must_complete_onboarding !== undefined) return Boolean(linkedAffiliate.must_complete_onboarding);
    const rows = linkedAffiliateLookupQuery.data?.results || [];
    const match = rows.find((u) => String(u.id) === String(linkedAffiliate.id));
    return Boolean(match?.must_complete_onboarding);
  }, [linkedAffiliate, linkedAffiliateLookupQuery.data]);

  const handleCopyAffiliateMessage = async () => {
    if (!selectedCoupon || !linkedAffiliate) return;
    setCopyNotice(null);
    const message = buildAffiliateCouponMessage({
      coupon: selectedCoupon,
      affiliateUsername: linkedAffiliate.username,
      origin: window.location.origin,
      includeAccountSetup: linkedAffiliateIsProvisional,
    });
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(message);
        setCopyNotice({ type: "success", message: "Copied affiliate message." });
      } else {
        throw new Error("Clipboard not available");
      }
    } catch (_e) {
      setCopyNotice({
        type: "error",
        message: "Could not copy automatically. Please copy the message manually.",
      });
    }
  };

  const validateSharedCreateFields = () => {
    const errors = {};

    // Free access: mode-driven (none / until a date / for a number of days).
    const freeAccessMode = form.free_access_mode || "none";
    if (form.is_access_coupon && freeAccessMode === "none") {
      errors.free_access_mode = "Access coupons must grant free access. Pick a free-access mode.";
    }
    if (freeAccessMode === "until") {
      if (!form.free_access_plan) errors.free_access_plan = "Select a free access plan.";
      if (!form.free_access_until) {
        errors.free_access_until = "Set a free access date.";
      } else {
        const until = new Date(form.free_access_until);
        if (Number.isNaN(until.getTime())) {
          errors.free_access_until = "Invalid date.";
        } else if (until.getTime() <= Date.now()) {
          errors.free_access_until = "Free access until must be in the future.";
        }
      }
    } else if (freeAccessMode === "days") {
      if (!form.free_access_plan) errors.free_access_plan = "Select a free access plan.";
      const days = Number(form.free_access_days);
      if (!form.free_access_days && form.free_access_days !== 0) {
        errors.free_access_days = "Set the number of free access days.";
      } else if (!Number.isFinite(days) || days < 1) {
        errors.free_access_days = "Free access days must be at least 1.";
      }
    }

    const trialDays = Number(form.free_trial_days || 0);
    const hasTrial = Number.isFinite(trialDays) && trialDays > 0;

    // Access coupons may be free-access-only: a percent/amount/trial is optional.
    const isAccess = Boolean(form.is_access_coupon);

    if (form.discountType === "trial") {
      if (!hasTrial && !isAccess) errors.free_trial_days = "Free trial period must be > 0 for trial-only coupons.";
      return errors;
    }

    if (form.discountType === "percent") {
      const v = Number(form.percent_off);
      if (!form.percent_off) {
        if (!isAccess) errors.percent_off = "Percent off is required.";
      } else if (Number.isNaN(v) || v <= 0 || v > 100) {
        errors.percent_off = "Percent off must be between 0 and 100.";
      }
    } else if (form.discountType === "amount") {
      const v = Number(form.amount_off);
      if (!form.amount_off) {
        if (!isAccess) errors.amount_off = "Amount off is required.";
      } else if (Number.isNaN(v) || v <= 0) {
        errors.amount_off = "Amount off must be > 0.";
      }
      if (form.amount_off && !form.currency?.trim()) errors.currency = "Currency is required for amount-off coupons.";
    } else {
      errors.discountType = "Select a discount type.";
    }

    return errors;
  };

  const validateCreateForm = () => {
    const errors = validateSharedCreateFields();
    if (!form.code.trim()) errors.code = "Code is required.";

    if (form.is_access_coupon) {
      const maxN = Number(form.max_redemptions);
      if (!form.max_redemptions || !Number.isInteger(maxN) || maxN < 1) {
        errors.max_redemptions = "Access coupons must have a max redemptions limit.";
      }
    }

    return errors;
  };

  const validateMultiCreateForm = () => {
    const errors = validateSharedCreateFields();

    const prefix = form.code.trim().toUpperCase();
    // Access-coupon bulk codes are PREFIX + 12 generated digits, so any prefix length works.
    if (!form.is_access_coupon && prefix && prefix.length >= COUPON_CODE_LENGTH) {
      errors.code = `Prefix must be shorter than ${COUPON_CODE_LENGTH} characters so digits can be appended.`;
    }

    const maxRaw = form.max_redemptions;
    const maxN = Number(maxRaw);
    if (!maxRaw && maxRaw !== 0) {
      errors.max_redemptions = "Max redemptions is required for multiple coupons (1–9).";
    } else if (!Number.isFinite(maxN) || maxN < 1 || maxN >= BULK_CREATE_MAX_REDEMPTIONS_EXCLUSIVE) {
      errors.max_redemptions = "Max redemptions must be between 1 and 9 for multiple coupons.";
    }

    const countN = Number(multiCount);
    if (!multiCount && multiCount !== 0) {
      errors.multiCount = `Count is required (1–${BULK_CREATE_MAX_COUNT}).`;
    } else if (!Number.isFinite(countN) || countN < 1 || countN > BULK_CREATE_MAX_COUNT) {
      errors.multiCount = `Count must be between 1 and ${BULK_CREATE_MAX_COUNT}.`;
    }

    return errors;
  };

  const resetCreateForm = () => {
    setFormErrors({});
    setForm({
      code: "",
      discountType: "percent",
      percent_off: "",
      amount_off: "",
      currency: DEFAULT_CURRENCY,
      include_renewals: false,
      free_trial_days: "0",
      redeem_by: "",
      max_redemptions: "",
      first_time_only: false,
      plans: [],
      billing_periods: [],
      active: true,
      affiliate_ids: [],
      free_access_mode: "none",
      free_access_until: "",
      free_access_days: "",
      free_access_plan: "",
      is_access_coupon: false,
      linked_coupon_id: null,
    });
    setCreateAffiliateQ("");
    setLinkedCouponQ("");
    setLinkedCouponSelection(null);
    setMultiCount(DEFAULT_MULTI_COUNT);
  };

  const buildFreeAccessPayload = () => {
    const freeAccessMode = form.free_access_mode || "none";
    if (freeAccessMode === "until") {
      return {
        free_access_until: form.free_access_until ? new Date(form.free_access_until).toISOString() : null,
        free_access_days: null,
        free_access_plan: form.free_access_plan || null,
      };
    }
    if (freeAccessMode === "days") {
      return {
        free_access_until: null,
        free_access_days: form.free_access_days ? Number(form.free_access_days) : null,
        free_access_plan: form.free_access_plan || null,
      };
    }
    return { free_access_until: null, free_access_days: null, free_access_plan: null };
  };

  const buildSharedCreatePayload = () => {
    const payload = {
      active: Boolean(form.active),
      include_renewals: Boolean(form.include_renewals),
      free_trial_days: Number(form.free_trial_days || 0),
      redeem_by: form.redeem_by ? new Date(form.redeem_by).toISOString() : null,
      max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      first_time_only: Boolean(form.first_time_only),
      plans: form.plans || [],
      billing_periods: form.billing_periods || [],
      influencer_ids: (form.affiliate_ids || []).map((x) => Number(x)).filter((x) => Number.isFinite(x)),
      is_access_coupon: Boolean(form.is_access_coupon),
      linked_coupon_id: form.linked_coupon_id != null ? Number(form.linked_coupon_id) : null,
      ...buildFreeAccessPayload(),
    };

    // Access coupons may be free-access-only, so only send a discount when one was entered.
    if (form.discountType === "percent" && form.percent_off !== "") {
      payload.percent_off = Number(form.percent_off);
    } else if (form.discountType === "amount" && form.amount_off !== "") {
      payload.amount_off = Number(form.amount_off);
      payload.currency = (form.currency || DEFAULT_CURRENCY).trim().toUpperCase();
    }

    return payload;
  };

  const buildCreatePayload = () => ({
    ...buildSharedCreatePayload(),
    code: form.code.trim(),
  });

  const buildBulkCreatePayload = () => {
    const prefix = form.code.trim();
    return {
      ...buildSharedCreatePayload(),
      count: Number(multiCount),
      code_prefix: prefix,
      max_redemptions: Number(form.max_redemptions),
    };
  };

  const formatCreateAuthHint = (status) => {
    if (status === 401) {
      return "Admin session is not authenticated (or expired). Try reloading the page and logging in again.";
    }
    if (status === 403) {
      return "Request was forbidden. If this is unexpected, your admin session may be invalid.";
    }
    return null;
  };

  // Free access configured but the access-coupon flag is off: the coupon would show
  // up as a regular coupon in the portal — ask the admin to confirm before creating.
  const needsAccessConfirm = () => (form.free_access_mode || "none") !== "none" && !form.is_access_coupon;

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setNotice(null);

    const errors = validateCreateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (needsAccessConfirm()) {
      setPendingAccessConfirm("single");
      return;
    }

    await submitCreateCoupon();
  };

  const submitCreateCoupon = async () => {
    try {
      const payload = buildCreatePayload();
      const result = await createMutation.mutateAsync(payload);

      if (result?.warning) {
        setNotice({ type: "warning", message: result.warning });
      } else {
        setNotice({ type: "success", message: "Coupon created successfully." });
      }

      resetCreateForm();

      // Ensure list reflects newest coupons and doesn't duplicate pages
      resetList();
    } catch (err) {
      const status = err?.status;
      const data = err?.data;
      const prettyData = data ? JSON.stringify(data, null, 2) : null;
      const fallback = err?.message || "Failed to create coupon.";
      const authHint = formatCreateAuthHint(status);

      setNotice({
        type: "error",
        message: status ? `Create coupon failed (HTTP ${status}): ${fallback}` : `Create coupon failed: ${fallback}`,
        details: [authHint, prettyData ? `Backend response:\n${prettyData}` : null].filter(Boolean).join("\n\n") || null,
      });
    }
  };

  const handleCreateMultipleCoupons = async () => {
    setNotice(null);

    const errors = validateMultiCreateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (needsAccessConfirm()) {
      setPendingAccessConfirm("bulk");
      return;
    }

    await submitCreateMultipleCoupons();
  };

  const submitCreateMultipleCoupons = async () => {
    try {
      const payload = buildBulkCreatePayload();
      const result = await createBulkMutation.mutateAsync(payload);
      const createdCount = result?.created_count ?? (result?.coupons || []).length;
      const syncWarnings = result?.sync_warnings || [];
      const otherErrors = (result?.errors || []).filter((e) => !String(e?.error || "").includes("Stripe sync failed"));

      if (syncWarnings.length || otherErrors.length) {
        const details = [
          syncWarnings.length ? `Stripe warnings:\n${syncWarnings.join("\n")}` : null,
          otherErrors.length ? `Other errors:\n${otherErrors.map((e) => e.error || JSON.stringify(e)).join("\n")}` : null,
        ]
          .filter(Boolean)
          .join("\n\n");
        setNotice({
          type: "warning",
          message: `Created ${createdCount} of ${payload.count} coupons with some issues.`,
          details: details || null,
        });
      } else {
        setNotice({
          type: "success",
          message: `Created ${createdCount} coupon${createdCount === 1 ? "" : "s"} successfully.`,
        });
      }

      resetCreateForm();
      resetList();
    } catch (err) {
      const status = err?.status;
      const data = err?.data;
      const prettyData = data ? JSON.stringify(data, null, 2) : null;
      const fallback = err?.message || data?.error || "Failed to create coupons.";
      const authHint = formatCreateAuthHint(status);

      setNotice({
        type: "error",
        message: status
          ? `Create multiple coupons failed (HTTP ${status}): ${fallback}`
          : `Create multiple coupons failed: ${fallback}`,
        details: [authHint, prettyData ? `Backend response:\n${prettyData}` : null].filter(Boolean).join("\n\n") || null,
      });
    }
  };

  const handleDeactivate = async () => {
    if (!selectedCouponId) return;
    setNotice(null);
    try {
      const result = await deactivateMutation.mutateAsync(selectedCouponId);
      if (result?.warning) setNotice({ type: "warning", message: result.warning });
      else setNotice({ type: "success", message: "Coupon deactivated." });
      resetList();
    } catch (err) {
      const status = err?.status;
      const data = err?.data;
      const prettyData = data ? JSON.stringify(data, null, 2) : null;
      const fallback = err?.message || "Failed to deactivate coupon.";

      setNotice({
        type: "error",
        message: status ? `Deactivate failed (HTTP ${status}): ${fallback}` : `Deactivate failed: ${fallback}`,
        details: prettyData ? `Backend response:\n${prettyData}` : null,
      });
    }
  };

  // Status color patterns (explicit dark variants allowed for semantic status states)
  const noticeStyles = {
    success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
    warning: "bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300",
    error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
  };

  const effectiveActiveBadgeClass = (isEffectivelyActive) => {
    if (isEffectivelyActive) {
      return "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300";
    }
    return `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`;
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className={`${typography.h2} ${textColors.primary}`}>Coupons</h1>
          <p className={`${typography.body} ${textColors.secondary} mt-1`}>Create, search and deactivate coupons.</p>
        </div>

        {/* Create coupon */}
        <Card variant="glass">
          <CardHeader
            title="Create Coupon"
            subtitle="All coupon attributes are stored in the DB and synced to Stripe as a Coupon + Promotion Code."
          />
          <CardBody>
            <form onSubmit={handleCreateCoupon} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormField
                  label="Code"
                  htmlFor="coupon-code"
                  required
                  helperText={
                    form.is_access_coupon
                      ? "Required for single create. For multiple access coupons, codes are your prefix + 12 generated digits."
                      : "Required for single create. For multiple coupons, optional prefix (digits fill up to 12)."
                  }
                >
                  <Input
                    id="coupon-code"
                    value={form.code}
                    onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                    error={formErrors.code}
                    placeholder="e.g. NEWYEAR10 or SUMMER"
                    maxLength={COUPON_CODE_LENGTH}
                    rightAdornment={
                      <IconButton
                        size="sm"
                        variant="ghost"
                        aria-label="Generate random coupon code"
                        title="Generate random coupon code"
                        icon={<ShuffleIcon size="sm" />}
                        onClick={() => {
                          const code = generateCouponCode(COUPON_CODE_LENGTH);
                          setForm((p) => ({ ...p, code }));
                          setFormErrors((p) => ({ ...p, code: undefined }));
                        }}
                      />
                    }
                  />
                </FormField>

                <FormField label="Redeem by" htmlFor="coupon-redeem-by" optional helperText="Optional. Must be in the future.">
                  <Input
                    id="coupon-redeem-by"
                    type="datetime-local"
                    value={form.redeem_by}
                    onChange={(e) => setForm((p) => ({ ...p, redeem_by: e.target.value }))}
                  />
                </FormField>

                <FormField
                  label="Max redemptions"
                  htmlFor="coupon-max-redemptions"
                  optional
                  helperText="Optional for single create. Required 1–9 when creating multiple coupons."
                >
                  <Input
                    id="coupon-max-redemptions"
                    type="number"
                    min="1"
                    value={form.max_redemptions}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, max_redemptions: e.target.value }));
                      setFormErrors((p) => ({ ...p, max_redemptions: undefined }));
                    }}
                    error={formErrors.max_redemptions}
                    placeholder="e.g. 100 (or 1–9 for multiple)"
                  />
                </FormField>

                <FormField
                  label="Free trial period (days)"
                  htmlFor="coupon-free-trial-days"
                  helperText="Optional. Applies only on free → paid checkout. Delays the first invoice by this many days."
                >
                  <Input
                    id="coupon-free-trial-days"
                    type="number"
                    min="0"
                    value={form.free_trial_days}
                    onChange={(e) => setForm((p) => ({ ...p, free_trial_days: e.target.value }))}
                    error={formErrors.free_trial_days}
                  />
                </FormField>

                <FormField
                  label="Free access"
                  htmlFor="coupon-free-access-mode"
                  optional
                  helperText="Optional. Grant a paid plan for free — until a fixed date or for a number of days from redemption."
                >
                  <Select
                    id="coupon-free-access-mode"
                    value={form.free_access_mode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((p) => ({
                        ...p,
                        free_access_mode: value,
                        // Clear the non-selected inputs so stale values don't leak into the payload.
                        free_access_until: value === "until" ? p.free_access_until : "",
                        free_access_days: value === "days" ? p.free_access_days : "",
                        free_access_plan: value === "none" ? "" : p.free_access_plan,
                        // Free access implies an access coupon by default (admin may uncheck);
                        // without free access an access coupon cannot exist.
                        is_access_coupon:
                          value === "none" ? false : p.free_access_mode === "none" ? true : p.is_access_coupon,
                      }));
                      setFormErrors((p) => ({
                        ...p,
                        free_access_mode: undefined,
                        free_access_until: undefined,
                        free_access_days: undefined,
                        free_access_plan: undefined,
                      }));
                    }}
                    options={[
                      { value: "none", label: "None" },
                      { value: "until", label: "Until a date" },
                      { value: "days", label: "For a number of days" },
                    ]}
                    placeholder={null}
                    error={formErrors.free_access_mode}
                  />
                </FormField>

                {form.free_access_mode === "until" ? (
                  <FormField
                    label="Free access until"
                    htmlFor="coupon-free-access-until"
                    required
                    helperText="Access ends at this date for everyone who redeems."
                  >
                    <Input
                      id="coupon-free-access-until"
                      type="datetime-local"
                      value={form.free_access_until}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((p) => ({ ...p, free_access_until: value }));
                        setFormErrors((p) => ({ ...p, free_access_until: undefined }));
                      }}
                      error={formErrors.free_access_until}
                    />
                  </FormField>
                ) : form.free_access_mode === "days" ? (
                  <FormField
                    label="Free access days"
                    htmlFor="coupon-free-access-days"
                    required
                    helperText="Access lasts this many days from the moment the coupon is redeemed."
                  >
                    <Input
                      id="coupon-free-access-days"
                      type="number"
                      min="1"
                      value={form.free_access_days}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((p) => ({ ...p, free_access_days: value }));
                        setFormErrors((p) => ({ ...p, free_access_days: undefined }));
                      }}
                      error={formErrors.free_access_days}
                      placeholder="e.g. 30"
                    />
                  </FormField>
                ) : (
                  <div className="hidden md:block" aria-hidden="true" />
                )}

                {form.free_access_mode !== "none" ? (
                  <FormField
                    label="Free access plan"
                    htmlFor="coupon-free-access-plan"
                    required
                    helperText="Paid plan granted for free during the free-access period."
                  >
                    <Select
                      id="coupon-free-access-plan"
                      value={form.free_access_plan}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((p) => ({ ...p, free_access_plan: value }));
                        setFormErrors((p) => ({ ...p, free_access_plan: undefined }));
                      }}
                      options={[
                        { value: "", label: "Select a plan" },
                        { value: "plus", label: "Plus" },
                        { value: "pro", label: "Pro" },
                      ]}
                      placeholder={null}
                      error={formErrors.free_access_plan}
                    />
                  </FormField>
                ) : (
                  <div className="hidden md:block" aria-hidden="true" />
                )}
              </div>

              {/* Access coupon flag + optional linked coupon */}
              <div className="space-y-3">
                <div>
                  <Checkbox
                    id="coupon-is-access"
                    checked={form.is_access_coupon}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((p) => ({ ...p, is_access_coupon: checked }));
                      setFormErrors((p) => ({ ...p, free_access_mode: undefined, max_redemptions: undefined }));
                    }}
                    label="Access coupon"
                  />
                  <p className={`mt-1 ${typography.caption} ${textColors.muted}`}>
                    Marks this as an access coupon: it grants plan access, shows under "Access Coupons" in the
                    portal, and requires a max redemptions limit.
                  </p>
                </div>

                {form.is_access_coupon && (
                  <div className="space-y-3">
                    <FormField
                      label="Link to coupon (optional)"
                      htmlFor="coupon-linked-coupon-search"
                      helperText="When someone redeems this access coupon, they are attributed to the linked coupon and it can apply at checkout later."
                    >
                      <SearchInput
                        id="coupon-linked-coupon-search"
                        value={linkedCouponQ}
                        onChange={(e) => setLinkedCouponQ(e.target.value)}
                        placeholder="Search coupon code…"
                      />
                    </FormField>

                    {form.linked_coupon_id != null && (
                      <div className="flex flex-wrap gap-2" aria-label="Selected linked coupon (create)">
                        <span
                          className={`inline-flex items-center gap-2 px-2 py-1 rounded-2xl ${surfaces.glass} ${borders.glass} border ${typography.caption} ${textColors.primary}`}
                        >
                          {linkedCouponSelection?.code || `Coupon #${form.linked_coupon_id}`}
                          <button
                            type="button"
                            className={`${textColors.secondary}`}
                            aria-label={`Remove linked coupon ${linkedCouponSelection?.code || form.linked_coupon_id}`}
                            onClick={() => {
                              setForm((p) => ({ ...p, linked_coupon_id: null }));
                              setLinkedCouponSelection(null);
                            }}
                          >
                            ×
                          </button>
                        </span>
                      </div>
                    )}

                    {(linkedCouponQ || "").trim().length >= 2 && (
                      <div
                        className={`border rounded-2xl ${borders.glass} ${surfaces.glassCard}`}
                        aria-label="Linked coupon search results (create)"
                      >
                        {linkedCouponQuery.isFetching ? (
                          <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>Searching…</div>
                        ) : linkedCouponResults.length === 0 ? (
                          <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>No coupons found.</div>
                        ) : (
                          <ul className="divide-y divide-gray-200 dark:divide-dark-700">
                            {linkedCouponResults.map((c) => {
                              const isSelected = String(form.linked_coupon_id) === String(c.id);
                              return (
                                <li key={c.id} className="flex items-center justify-between px-3 py-2">
                                  <div className="min-w-0">
                                    <p className={`${typography.bodySmall} ${textColors.primary}`}>{c.code}</p>
                                    <p className={`${typography.caption} ${textColors.muted}`}>{formatDiscount(c)}</p>
                                  </div>
                                  <SecondaryButton
                                    type="button"
                                    onClick={() => {
                                      // Single linked coupon: selecting replaces any previous choice.
                                      setForm((p) => ({ ...p, linked_coupon_id: isSelected ? null : c.id }));
                                      setLinkedCouponSelection(isSelected ? null : { id: c.id, code: c.code });
                                    }}
                                  >
                                    {isSelected ? "Remove" : "Select"}
                                  </SecondaryButton>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Divider />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                  <FormField label="Discount type" htmlFor="discount-type">
                    <RadioGroup
                      name="discount-type"
                      value={form.discountType}
                      onChange={(value) => {
                        setFormErrors((p) => ({ ...p, discountType: undefined }));
                        setForm((prev) => ({ ...prev, discountType: value }));
                      }}
                      options={[
                        { value: "percent", label: "Percent off" },
                        { value: "amount", label: "Amount off" },
                        { value: "trial", label: "Free trial only" },
                      ]}
                      error={formErrors.discountType}
                      orientation="vertical"
                      aria-label="Discount type"
                    />
                  </FormField>
                </div>

                <div className="lg:col-span-2">
                  {form.discountType === "trial" ? (
                    <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                      This coupon provides a free trial period (no price discount).
                    </div>
                  ) : form.discountType === "percent" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField label="Percent off" htmlFor="coupon-percent-off" required>
                        <Input
                          id="coupon-percent-off"
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={form.percent_off}
                          onChange={(e) => setForm((p) => ({ ...p, percent_off: e.target.value }))}
                          error={formErrors.percent_off}
                          placeholder="e.g. 10"
                        />
                      </FormField>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField label="Amount off" htmlFor="coupon-amount-off" required>
                        <Input
                          id="coupon-amount-off"
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.amount_off}
                          onChange={(e) => setForm((p) => ({ ...p, amount_off: e.target.value }))}
                          error={formErrors.amount_off}
                          placeholder="e.g. 5.00"
                        />
                      </FormField>
                      <FormField label="Currency" htmlFor="coupon-currency" required>
                        <Input
                          id="coupon-currency"
                          value={form.currency}
                          onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                          error={formErrors.currency}
                          placeholder="USD"
                        />
                      </FormField>
                    </div>
                  )}
                </div>
              </div>

              <Divider />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-1">
                  <CardBody className="space-y-3">
                    <p className={`${typography.label} ${textColors.secondary}`}>Options</p>
                    <Checkbox
                      checked={form.active}
                      onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                      label="Active (initial)"
                    />
                    <Checkbox
                      checked={form.include_renewals}
                      onChange={(e) => setForm((p) => ({ ...p, include_renewals: e.target.checked }))}
                      label="Include renewals"
                    />
                    <Checkbox
                      checked={form.first_time_only}
                      onChange={(e) => setForm((p) => ({ ...p, first_time_only: e.target.checked }))}
                      label="First-time only"
                    />
                  </CardBody>
                </Card>

                <Card className="lg:col-span-1">
                  <CardBody className="space-y-3">
                    <p className={`${typography.label} ${textColors.secondary}`}>Plans (optional)</p>
                    <Checkbox
                      checked={(form.plans || []).includes("free")}
                      onChange={() => setForm((p) => ({ ...p, plans: toggleArrayValue(p.plans, "free") }))}
                      label="Free"
                    />
                    <Checkbox
                      checked={(form.plans || []).includes("plus")}
                      onChange={() => setForm((p) => ({ ...p, plans: toggleArrayValue(p.plans, "plus") }))}
                      label="Plus"
                    />
                    <Checkbox
                      checked={(form.plans || []).includes("pro")}
                      onChange={() => setForm((p) => ({ ...p, plans: toggleArrayValue(p.plans, "pro") }))}
                      label="Pro"
                    />
                    <p className={`${typography.caption} ${textColors.muted}`}>Leave empty for all plans.</p>
                  </CardBody>
                </Card>

                <Card className="lg:col-span-1">
                  <CardBody className="space-y-3">
                    <p className={`${typography.label} ${textColors.secondary}`}>Billing periods (optional)</p>
                    <Checkbox
                      checked={(form.billing_periods || []).includes("monthly")}
                      onChange={() =>
                        setForm((p) => ({ ...p, billing_periods: toggleArrayValue(p.billing_periods, "monthly") }))
                      }
                      label="Monthly"
                    />
                    <Checkbox
                      checked={(form.billing_periods || []).includes("yearly")}
                      onChange={() =>
                        setForm((p) => ({ ...p, billing_periods: toggleArrayValue(p.billing_periods, "yearly") }))
                      }
                      label="Yearly"
                    />
                    <p className={`${typography.caption} ${textColors.muted}`}>Leave empty for all billing periods.</p>
                  </CardBody>
                </Card>
              </div>

              <Divider />

              {/* Affiliate (single) */}
              <div className="space-y-3">
                <FormField
                  label="Affiliate (optional)"
                  htmlFor="coupon-affiliate-search"
                  helperText="Type at least 2 characters to search affiliate users. At most one affiliate can be assigned; selecting a new one replaces the current."
                >
                  <SearchInput
                    id="coupon-affiliate-search"
                    value={createAffiliateQ}
                    onChange={(e) => setCreateAffiliateQ(e.target.value)}
                    placeholder="Search affiliate username/email…"
                  />
                </FormField>

                {!!(form.affiliate_ids || []).length && (
                  <div className="flex flex-wrap gap-2" aria-label="Selected affiliate (create)">
                    {(form.affiliate_ids || []).map((id) => {
                      const u =
                        createAffiliateResults.find((x) => String(x.id) === String(id)) ||
                        { id, username: `User #${id}` };
                      return (
                        <span
                          key={String(id)}
                          className={`inline-flex items-center gap-2 px-2 py-1 rounded-2xl ${surfaces.glass} ${borders.glass} border ${typography.caption} ${textColors.primary}`}
                        >
                          {u.username}
                          <button
                            type="button"
                            className={`${textColors.secondary}`}
                            aria-label={`Remove affiliate ${u.username}`}
                            onClick={() =>
                              setForm((p) => ({
                                ...p,
                                affiliate_ids: (p.affiliate_ids || []).filter((x) => String(x) !== String(id)),
                              }))
                            }
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {(createAffiliateQ || "").trim().length >= 2 && (
                  <div
                    className={`border rounded-2xl ${borders.glass} ${surfaces.glassCard}`}
                    aria-label="Affiliate search results (create)"
                  >
                    {affiliateCreateQuery.isFetching ? (
                      <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>Searching…</div>
                    ) : createAffiliateResults.length === 0 ? (
                      <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>No affiliate users found.</div>
                    ) : (
                      <ul className="divide-y divide-gray-200 dark:divide-dark-700">
                        {createAffiliateResults.map((u) => {
                          const isSelected = (form.affiliate_ids || []).some((x) => String(x) === String(u.id));
                          return (
                            <li key={u.id} className="flex items-center justify-between px-3 py-2">
                              <div className="min-w-0">
                                <p className={`${typography.bodySmall} ${textColors.primary}`}>{u.username}</p>
                                <p className={`${typography.caption} ${textColors.muted}`}>{u.email}</p>
                              </div>
                              <SecondaryButton
                                type="button"
                                onClick={() =>
                                  setForm((p) => ({
                                    ...p,
                                    // Single affiliate: selecting replaces any previous choice.
                                    affiliate_ids: isSelected ? [] : [u.id],
                                  }))
                                }
                              >
                                {isSelected ? "Remove" : "Select"}
                              </SecondaryButton>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {notice && (
                <div className={`rounded-2xl px-4 py-3 ${noticeStyles[notice.type]}`}>
                  <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
                  {notice.details && (
                    <pre
                      className={`mt-2 ${typography.caption} whitespace-pre-wrap break-words font-mono ${textColors.secondary}`}
                      aria-label="Admin error details"
                    >
                      {notice.details}
                    </pre>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-end gap-3">
                <SecondaryButton type="button" onClick={() => setFormErrors({})} disabled={isCreateBusy}>
                  Clear errors
                </SecondaryButton>
                <div className="flex items-end gap-2">
                  <FormField label="Count" htmlFor="coupon-multi-count" helperText={`1–${BULK_CREATE_MAX_COUNT}`}>
                    <Input
                      id="coupon-multi-count"
                      type="number"
                      min="1"
                      max={BULK_CREATE_MAX_COUNT}
                      value={multiCount}
                      onChange={(e) => {
                        setMultiCount(e.target.value);
                        setFormErrors((p) => ({ ...p, multiCount: undefined }));
                      }}
                      error={formErrors.multiCount}
                      aria-label="Number of coupons to create"
                      className="w-20"
                    />
                  </FormField>
                  <PrimaryButton
                    type="button"
                    loading={createBulkMutation.isPending}
                    disabled={isCreateBusy}
                    onClick={handleCreateMultipleCoupons}
                  >
                    Create multiple
                  </PrimaryButton>
                </div>
                <PrimaryButton type="submit" loading={createMutation.isPending} disabled={isCreateBusy}>
                  Create coupon
                </PrimaryButton>
              </div>
            </form>
          </CardBody>
        </Card>

        {/* Filters + list */}
        <Card variant="glass">
          <CardHeader title="Coupons" subtitle="Search and filter coupons (effective active is computed by the backend)." />
          <CardBody className="space-y-4">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex-1">
                <FormField label="Search" htmlFor="coupon-search" optional>
                  <SearchInput
                    id="coupon-search"
                    value={qInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setQInput(value);
                      debouncedSetQ(value);
                    }}
                    placeholder="Search code or Stripe IDs…"
                  />
                </FormField>
              </div>
              <div className="w-full md:w-64">
                <FormField label="Active state" htmlFor="coupon-active-state">
                  <Select
                    id="coupon-active-state"
                    value={activeState}
                    onChange={(e) => setActiveState(e.target.value)}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                      { value: "all", label: "All" },
                    ]}
                    placeholder={null}
                  />
                </FormField>
              </div>
              <div className="flex gap-2">
                <SecondaryButton
                  type="button"
                  onClick={() => {
                    setQInput("");
                    setQ("");
                    setActiveState("all");
                  }}
                >
                  Clear filters
                </SecondaryButton>
              </div>
            </div>

            <Divider />

            <div className={isDetailsOpen ? "grid grid-cols-1 xl:grid-cols-3 gap-4" : undefined}>
              {/* Details — left pane, only when a coupon is selected */}
              {isDetailsOpen && (
              <div className="xl:col-span-1 order-1">
                <Card variant="glass">
                  <CardHeader
                    title="Details"
                    subtitle={`Coupon #${selectedCoupon.id}`}
                    actions={
                      <SecondaryButton type="button" onClick={() => setSelectedCouponId(null)}>
                        Close
                      </SecondaryButton>
                    }
                  />
                  <CardBody className="space-y-3">
                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Code</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>{selectedCoupon.code}</p>
                        </div>

                        {couponShareLink && (
                          <div className="space-y-1">
                            <p className={`${typography.caption} ${textColors.muted}`}>Coupon link</p>
                            <div className="flex items-center gap-2 min-w-0">
                              <a
                                href={couponShareLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${typography.bodySmall} ${textColors.primary} underline break-all`}
                              >
                                {couponShareLink}
                              </a>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label="Copy coupon link"
                                title="Copy"
                                icon={<CopyIcon size="sm" />}
                                onClick={async () => {
                                  setCopyNotice(null);
                                  try {
                                    if (navigator?.clipboard?.writeText) {
                                      await navigator.clipboard.writeText(couponShareLink);
                                      setCopyNotice({ type: "success", message: "Copied link." });
                                    } else {
                                      throw new Error("Clipboard not available");
                                    }
                                  } catch (_e) {
                                    setCopyNotice({
                                      type: "error",
                                      message: "Could not copy automatically. Please copy the link manually.",
                                    });
                                  }
                                }}
                              />
                            </div>
                            {copyNotice && (
                              <div className={`rounded-2xl px-3 py-2 ${noticeStyles[copyNotice.type]}`}>
                                <p className={`${typography.bodySmall}`}>{copyNotice.message}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {linkedAffiliate && (
                          <div className="space-y-1">
                            <p className={`${typography.caption} ${textColors.muted}`}>Message for affiliate</p>
                            <SecondaryButton
                              type="button"
                              onClick={handleCopyAffiliateMessage}
                              className="w-full whitespace-nowrap"
                            >
                              {linkedAffiliateIsProvisional ? "Copy message (with account setup)" : "Copy message"}
                            </SecondaryButton>
                            <p className={`${typography.caption} ${textColors.muted}`}>
                              {linkedAffiliateIsProvisional
                                ? "Includes the coupon link and the account-claim steps. Paste the temporary password over the placeholder before sending."
                                : "Includes the coupon link and how to track performance."}
                            </p>
                          </div>
                        )}

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Discount</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>{formatDiscount(selectedCoupon)}</p>
                        </div>

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Effective active</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {selectedCoupon.is_effectively_active ? "Active" : "Inactive"}
                          </p>
                        </div>

                        <Divider />

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Plans</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {(selectedCoupon.plans || []).length ? (selectedCoupon.plans || []).join(", ") : "All"
                            }
                          </p>
                        </div>

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Billing periods</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {(selectedCoupon.billing_periods || []).length
                              ? (selectedCoupon.billing_periods || []).join(", ")
                              : "All"}
                          </p>
                        </div>

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Free access</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {selectedCoupon.free_access_until
                              ? `Until ${formatIsoDateTime(selectedCoupon.free_access_until)}`
                              : selectedCoupon.free_access_days
                              ? `${selectedCoupon.free_access_days} day${Number(selectedCoupon.free_access_days) === 1 ? "" : "s"} from redemption`
                              : "—"}
                          </p>
                        </div>

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Free access plan</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {selectedCoupon.free_access_plan || "—"}
                          </p>
                        </div>

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Access coupon</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {selectedCoupon.is_access_coupon ? "Yes" : "No"}
                          </p>
                        </div>

                        {selectedCoupon.linked_coupon_code && (
                          <div className="space-y-1">
                            <p className={`${typography.caption} ${textColors.muted}`}>Linked coupon</p>
                            <p className={`${typography.bodySmall} ${textColors.primary}`}>
                              {selectedCoupon.linked_coupon_code}
                            </p>
                          </div>
                        )}

                        <Divider />

                        <div className="space-y-2">
                          <p className={`${typography.caption} ${textColors.muted}`}>Affiliate</p>

                          {(selectedCouponAffiliates || []).length ? (
                            <div className="flex flex-wrap gap-2" aria-label="Selected affiliate (details)">
                              {(selectedCouponAffiliates || []).map((u) => (
                                <span
                                  key={String(u.id)}
                                  className={`inline-flex items-center px-2 py-1 rounded-2xl ${surfaces.glass} ${borders.glass} border ${typography.caption} ${textColors.primary}`}
                                >
                                  {u.username}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className={`${typography.bodySmall} ${textColors.secondary}`}>None</p>
                          )}

                          <Divider />

                          <FormField
                            label="Edit affiliate"
                            htmlFor="coupon-details-affiliate-search"
                            helperText="Search affiliate users and pick at most one (selecting a new one replaces the current), then Save."
                          >
                            <SearchInput
                              id="coupon-details-affiliate-search"
                              value={detailsAffiliateQ}
                              onChange={(e) => setDetailsAffiliateQ(e.target.value)}
                              placeholder="Search affiliate username/email…"
                            />
                          </FormField>

                          {!!(detailsAffiliateIds || []).length && (
                            <div className="flex flex-wrap gap-2" aria-label="Affiliate draft (details)">
                              {(detailsDraftAffiliates || []).map((u) => (
                                <span
                                  key={String(u.id)}
                                  className={`inline-flex items-center gap-2 px-2 py-1 rounded-2xl ${surfaces.glass} ${borders.glass} border ${typography.caption} ${textColors.primary}`}
                                >
                                  {u.username}
                                  <button
                                    type="button"
                                    aria-label={`Remove affiliate ${u.username} from coupon`}
                                    className={`${textColors.secondary}`}
                                    onClick={() =>
                                      setDetailsAffiliateIds((prev) => (prev || []).filter((x) => String(x) !== String(u.id)))
                                    }
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}

                          {(detailsAffiliateQ || "").trim().length >= 2 && (
                            <div
                              className={`border rounded-2xl ${borders.glass} ${surfaces.glassCard}`}
                              aria-label="Affiliate search results (details)"
                            >
                              {affiliateDetailsQuery.isFetching ? (
                                <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>Searching…</div>
                              ) : detailsAffiliateResults.length === 0 ? (
                                <div className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>No affiliate users found.</div>
                              ) : (
                                <ul className="divide-y divide-gray-200 dark:divide-dark-700">
                                  {detailsAffiliateResults.map((u) => {
                                    const isSelected = (detailsAffiliateIds || []).some((x) => String(x) === String(u.id));
                                    return (
                                      <li key={u.id} className="flex items-center justify-between px-3 py-2">
                                        <div className="min-w-0">
                                          <p className={`${typography.bodySmall} ${textColors.primary}`}>{u.username}</p>
                                          <p className={`${typography.caption} ${textColors.muted}`}>{u.email}</p>
                                        </div>
                                        <SecondaryButton
                                          type="button"
                                          onClick={() =>
                                            // Single affiliate: selecting replaces any previous choice.
                                            setDetailsAffiliateIds(isSelected ? [] : [u.id])
                                          }
                                        >
                                          {isSelected ? "Remove" : "Select"}
                                        </SecondaryButton>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </div>
                          )}

                          {detailsAffiliateNotice && (
                            <div className={`rounded-2xl px-3 py-2 ${noticeStyles[detailsAffiliateNotice.type]}`}>
                              <p className={`${typography.bodySmall}`}>{detailsAffiliateNotice.message}</p>
                            </div>
                          )}

                          <div className="flex items-center justify-end gap-2">
                            {affiliateIdsAreDirty && (
                              <>
                                <SecondaryButton
                                  type="button"
                                  onClick={() => {
                                    setDetailsAffiliateIds(selectedCoupon?.influencer_ids || []);
                                    setDetailsAffiliateNotice(null);
                                  }}
                                  disabled={detailsAffiliateSaving}
                                >
                                  Reset
                                </SecondaryButton>
                                <PrimaryButton
                                  type="button"
                                  loading={detailsAffiliateSaving}
                                  onClick={() => setIsConfirmAffiliateSaveOpen(true)}
                                >
                                  Save
                                </PrimaryButton>
                              </>
                            )}
                          </div>
                        </div>

                        <Divider />

                        <div className="space-y-1">
                          <p className={`${typography.caption} ${textColors.muted}`}>Manual deactivated at</p>
                          <p className={`${typography.bodySmall} ${textColors.primary}`}>
                            {formatIsoDateTime(selectedCoupon.manual_deactivated_at)}
                          </p>
                        </div>

                        {!selectedCoupon.manual_deactivated_at && (
                          <DangerButton type="button" onClick={() => setIsDeactivateOpen(true)} loading={deactivateMutation.isPending}>
                            Deactivate
                          </DangerButton>
                        )}
                  </CardBody>
                </Card>
              </div>
              )}

              {/* Table — full width by default; condensed on the right when details are open */}
              <div className={isDetailsOpen ? "xl:col-span-2 order-2" : undefined}>
                <div className={`border rounded-2xl overflow-hidden ${borders.glass}`}>
                  <div className={`overflow-x-auto ${surfaces.glassCard}`}>
                    <table className="min-w-full text-left">
                      <thead className={`${surfaces.glass}`}>
                        <tr className={`${textColors.secondary} ${typography.caption}`}>
                          <th className="px-3 py-2 w-10">Sel</th>
                          <th className="px-3 py-2">Code</th>
                          <th className="px-3 py-2">Discount</th>
                          <th className="px-3 py-2">Effective Active</th>
                          {!isDetailsOpen && <th className="px-3 py-2">Redeem by</th>}
                          <th className="px-3 py-2">Redemptions</th>
                          {!isDetailsOpen && (
                            <>
                              <th className="px-3 py-2">Renewals</th>
                              <th className="px-3 py-2">+Months</th>
                              <th className="px-3 py-2">Manual deactivated</th>
                              <th className="px-3 py-2">Created</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className={`${surfaces.glassCard}`}>
                        {couponsQuery.isLoading && (
                          <tr>
                            <td colSpan={tableColSpan} className={`px-3 py-6 ${textColors.secondary} ${typography.bodySmall}`}>
                              Loading coupons…
                            </td>
                          </tr>
                        )}

                        {couponsQuery.error && (
                          <tr>
                            <td colSpan={tableColSpan} className={`px-3 py-6 ${textColors.secondary} ${typography.bodySmall}`}>
                              Failed to load coupons: {couponsQuery.error?.message || "Unknown error"}
                            </td>
                          </tr>
                        )}

                        {!couponsQuery.isLoading && !couponsQuery.error && coupons.length === 0 && (
                          <tr>
                            <td colSpan={tableColSpan} className={`px-3 py-6 ${textColors.secondary} ${typography.bodySmall}`}>
                              No coupons found.
                            </td>
                          </tr>
                        )}

                        {coupons.map((c) => {
                          const isSelected = c.id === selectedCouponId;
                          return (
                            <tr
                              key={c.id}
                              className={`${borders.glass} border-t ${surfaces.hover} ${isSelected ? surfaces.active : ""} cursor-pointer`}
                              onClick={() => setSelectedCouponId(isSelected ? null : c.id)}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => setSelectedCouponId(isSelected ? null : c.id)}
                                  aria-label={`Select coupon ${c.code}`}
                                  className="accent-blue-600 dark:accent-blue-500"
                                />
                              </td>
                              <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.primary}`}>
                                <span className="inline-flex items-center gap-2">
                                  {c.code}
                                  {c.is_access_coupon && (
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300`}
                                    >
                                      Access
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>{formatDiscount(c)}</td>
                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full ${typography.caption} ${effectiveActiveBadgeClass(
                                    c.is_effectively_active
                                  )}`}
                                >
                                  {c.is_effectively_active ? "Active" : "Inactive"}
                                </span>
                              </td>
                              {!isDetailsOpen && (
                                <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>{formatIsoDateTime(c.redeem_by)}</td>
                              )}
                              <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>{formatRedemptions(c)}</td>
                              {!isDetailsOpen && (
                                <>
                                  <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>{c.include_renewals ? "Yes" : "No"}</td>
                                  <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>{c.free_trial_days ?? 0}</td>
                                  <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>
                                    {formatIsoDateTime(c.manual_deactivated_at)}
                                  </td>
                                  <td className={`px-3 py-2 ${typography.bodySmall} ${textColors.secondary}`}>{formatIsoDateTime(c.created_at)}</td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3">
                  <p className={`${typography.caption} ${textColors.muted}`}>Loaded: {coupons.length}</p>
                  <div className="flex items-center gap-2">
                    <SecondaryButton type="button" onClick={() => resetList()} disabled={couponsQuery.isFetching}>
                      Refresh
                    </SecondaryButton>
                    {nextCursor ? (
                      <PrimaryButton
                        type="button"
                        onClick={() => setCursor(nextCursor)}
                        loading={couponsQuery.isFetching}
                      >
                        Load more
                      </PrimaryButton>
                    ) : (
                      <SecondaryButton type="button" disabled>
                        No more
                      </SecondaryButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Global default coupon templates (collapsed by default, like the pack-pricing card) */}
        <CouponDefaultTemplateCard kind="regular" />
        <CouponDefaultTemplateCard kind="access" />

        <ConfirmDialog
          isOpen={pendingAccessConfirm != null}
          onClose={() => setPendingAccessConfirm(null)}
          onConfirm={async () => {
            const action = pendingAccessConfirm;
            setPendingAccessConfirm(null);
            if (action === "single") await submitCreateCoupon();
            else if (action === "bulk") await submitCreateMultipleCoupons();
          }}
          title="Create as a regular coupon?"
          message="This coupon grants free access but is not marked as an access coupon — it will appear as a regular coupon in the portal. Create anyway?"
          variant="info"
          confirmText="Create anyway"
          cancelText="Cancel"
          isLoading={isCreateBusy}
        />

        <ConfirmDialog
          isOpen={isDeactivateOpen}
          onClose={() => setIsDeactivateOpen(false)}
          onConfirm={handleDeactivate}
          title="Deactivate coupon?"
          message="This will set manual_deactivated_at immediately and sync the change to Stripe."
          variant="danger"
          confirmText="Deactivate"
          cancelText="Cancel"
          isLoading={deactivateMutation.isPending}
        />

        <ConfirmDialog
          isOpen={isConfirmAffiliateSaveOpen}
          onClose={() => setIsConfirmAffiliateSaveOpen(false)}
          onConfirm={async () => {
            if (!selectedCoupon?.id) return;
            setIsConfirmAffiliateSaveOpen(false);
            setDetailsAffiliateNotice(null);
            setDetailsAffiliateSaving(true);
            try {
              await setCouponAffiliates(selectedCoupon.id, detailsAffiliateIds || []);
              setDetailsAffiliateNotice({ type: "success", message: "Coupon affiliates updated." });
              resetList();
              setSelectedCouponId(selectedCoupon.id);
            } catch (err) {
              setDetailsAffiliateNotice({
                type: "error",
                message: err?.message || "Failed to update coupon affiliates.",
              });
            } finally {
              setDetailsAffiliateSaving(false);
            }
          }}
          title="Save affiliate changes?"
          message="This will update which affiliate is linked to this coupon."
          variant="info"
          confirmText="Save changes"
          cancelText="Cancel"
          isLoading={detailsAffiliateSaving}
        />
      </div>
    </div>
  );
};

export default CouponsAdminPage;


